import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import { motion, AnimatePresence } from 'framer-motion';
import apiService from '../services/api';
import CommentsModal from '../components/CommentsModal';
import VideoCard from '../components/VideoCard';

/**
 * TikTok-style vertical scrolling video feed
 * Auto-plays videos, tracks watch time, retention, and engagement
 */
const SAFE_BOTTOM_OFFSET = 120;
const VIEW_SECONDS_THRESHOLD = 3;
const VIEW_COMPLETION_THRESHOLD = 0.35; // 35% of video duration

const isHttpUrl = (url = '') => /^http:\/\//i.test(url);

const normalizeMediaUrl = (url, secureContext) => {
  if (!url) return '';
  if (!secureContext) return url;
  if (url.startsWith('https://') || url.startsWith('//')) return url;
  if (isHttpUrl(url)) {
    return url.replace('http://', 'https://');
  }
  return url;
};

const getCreatorId = (post = {}) =>
  post.user_id ?? post.userId ?? post.creator_id ?? post.creatorId;

const updatePostCollection = (posts = [], postId, updater) =>
  posts.map((post) => (post.id === postId ? updater(post) : post));

const guessVideoMimeType = (url = '') => {
  const cleanUrl = url.split('?')[0].toLowerCase();
  if (cleanUrl.endsWith('.mp4')) return 'video/mp4';
  if (cleanUrl.endsWith('.webm')) return 'video/webm';
  if (cleanUrl.endsWith('.mov')) return 'video/quicktime';
  if (cleanUrl.endsWith('.mkv')) return 'video/x-matroska';
  return null;
};
export function Feed() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [secureContext, setSecureContext] = useState(() =>
    typeof window !== 'undefined' ? window.location.protocol === 'https:' : false
  );
  const [posts, setPosts] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [showMixedContentWarning, setShowMixedContentWarning] = useState(false);
  const [soundOn, setSoundOn] = useState(false);
  const [commentsModal, setCommentsModal] = useState({ isOpen: false, post: null });
  
  // Feed type filter
  const [feedType, setFeedType] = useState('all'); // 'all', 'video', 'music', 'art', 'stories'
  
  const containerRef = useRef(null);
  const videoRefs = useRef({});
  const observerRef = useRef(null);
  const prefetchLinksRef = useRef(new Map());
  const viewTrackerRef = useRef(new Set());
  const watchProgressRef = useRef({});
  const transformPosts = useCallback(
    (incoming = []) =>
      incoming.map((post) => ({
        ...post,
        content_url: normalizeMediaUrl(post.content_url, secureContext),
      })),
    [secureContext]
  );

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setSecureContext(window.location.protocol === 'https:');
    }
  }, []);
  const actionStackStyle = useMemo(
    () => ({
      bottom: `calc(20px + var(--safe-area-bottom, 0px))`,
    }),
    []
  );

  const registerVideoRef = useCallback(
    (postId) => (node) => {
      if (node) {
        videoRefs.current[postId] = node;
      } else {
        delete videoRefs.current[postId];
      }
    },
    []
  );

  const cleanupPrefetchLinks = useCallback(() => {
    if (typeof document === 'undefined') return;
    prefetchLinksRef.current.forEach((link) => {
      if (link?.parentNode) {
        link.parentNode.removeChild(link);
      }
    });
    prefetchLinksRef.current.clear();
  }, []);

  useEffect(() => () => cleanupPrefetchLinks(), [cleanupPrefetchLinks]);

  /**
   * Smart prefetch: Only prefetch the immediately next video
   * Uses prefetch (not preload) to avoid "not used in time" warnings
   * Prefetch is a hint to the browser to download when idle, not a demand
   */
  const prefetchMedia = useCallback((url) => {
    if (!url || typeof document === 'undefined' || prefetchLinksRef.current.has(url)) {
      return;
    }

    const link = document.createElement('link');
    // Always use prefetch - it doesn't trigger "not used in time" warnings
    // and works better for video content that may or may not be viewed
    link.rel = 'prefetch';
    link.href = url;
    link.crossOrigin = 'anonymous';

    const mime = guessVideoMimeType(url);
    if (mime) {
      link.type = mime;
    }

    document.head.appendChild(link);
    prefetchLinksRef.current.set(url, link);
  }, []);

  // Smart prefetch: only prefetch the immediately next video (not 2 ahead)
  // This reduces bandwidth waste for large 4K videos that may never be viewed
  useEffect(() => {
    // Only prefetch the very next video, not multiple ahead
    const nextPost = posts[currentIndex + 1];
    if (nextPost) {
      const src = normalizeMediaUrl(nextPost.content_url, secureContext);
      prefetchMedia(src);
    }
  }, [currentIndex, posts, secureContext, prefetchMedia]);

  // Load initial feed
  useEffect(() => {
    loadFeed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Reload when feed type changes
  useEffect(() => {
    setPosts([]);
    setCurrentIndex(0);
    setHasMore(true);
    loadFeed(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedType]);

  // Setup Intersection Observer to auto-play videos when they come into view
  useEffect(() => {
    if (posts.length === 0) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          const index = parseInt(video.dataset.index);

          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            // Make sure only one video plays at any given time
            Object.values(videoRefs.current).forEach((refVideo) => {
              if (!refVideo || refVideo === video) return;
              refVideo.pause();
              // Don't reset time here to avoid "skipping" feel if user scrolls back
            });

            video.play().catch((err) => console.log('Auto-play failed:', err));
            setCurrentIndex(index);

            // Preload next video aggressively
            const nextVideo = videoRefs.current[posts[index + 1]?.id];
            if (nextVideo) {
              nextVideo.preload = 'auto';
            }
          } else {
            // Video scrolled out of view - pause but don't reset immediately
            video.pause();
          }
        });
      },
      { threshold: [0.5] } // Only trigger at 50% visibility
    );

    // Observe all videos
    Object.values(videoRefs.current).forEach((video) => {
      if (video) observerRef.current.observe(video);
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [posts, prefetchMedia, secureContext]);

  useEffect(() => {
    const activePostId = posts[currentIndex]?.id;
    Object.entries(videoRefs.current).forEach(([postId, video]) => {
      if (!video) return;
      const isActive = String(postId) === String(activePostId);
      const shouldMute = !soundOn || !isActive;

      video.muted = shouldMute;

      if (!isActive) {
        video.pause();
        video.currentTime = 0;
      } else if (!shouldMute && video.paused) {
        video.play().catch((err) => console.log('Resume active video failed:', err));
      }
    });
  }, [soundOn, currentIndex, posts]);

  const toggleSound = useCallback(() => {
    setSoundOn((prev) => !prev);
  }, []);

  const openComments = useCallback((post) => {
    setCommentsModal({ isOpen: true, post });
  }, []);

  const closeComments = useCallback(() => {
    setCommentsModal({ isOpen: false, post: null });
  }, []);

  const loadFeed = async (reset = false) => {
    if (!reset && posts.length > 0 && (loading || !hasMore)) return;

    setLoading(true);
    try {
      const offset = reset ? 0 : posts.length;
      const params = {
        limit: 10,
        offset,
      };
      
      // Add feed type filter if not 'all'
      if (feedType && feedType !== 'all') {
        params.feed_type = feedType;
      }
      
      const response = await apiService.getFeed(params);

      console.log('📥 Feed response:', response);

      if (Array.isArray(response) && response.length > 0) {
        const normalizedPosts = transformPosts(response);

        if (
          secureContext &&
          normalizedPosts.some((item) => isHttpUrl(item?.content_url) || isHttpUrl(item?.video_url))
        ) {
          setShowMixedContentWarning(true);
        }

        if (reset) {
          setPosts(normalizedPosts);
        } else {
          setPosts((prev) => [...prev, ...normalizedPosts]);
        }
        setHasMore(response.length === 10);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('❌ Failed to load feed:', error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  // Track video engagement
  const trackEngagement = useCallback(async (post, engagementType, metadata = {}) => {
    if (!user && engagementType !== 'view') return;

    try {
      await apiService.trackEngagement({
        postId: post.id,
        actionType: engagementType,
        ...metadata
      });

      // Update local post state
      if (engagementType === 'like' || engagementType === 'unlike') {
        setPosts((prev) =>
          updatePostCollection(prev, post.id, (current) => ({
            ...current,
            like_count: Math.max(
              (current.like_count || 0) + (engagementType === 'like' ? 1 : -1),
              0
            ),
            is_liked: engagementType === 'like',
          }))
        );
      }

      if (engagementType === 'view') {
        setPosts((prev) =>
          updatePostCollection(prev, post.id, (current) => ({
            ...current,
            view_count: (current.view_count || 0) + 1,
          }))
        );
      }
    } catch (error) {
      console.error('Failed to track engagement:', error);
    }
  }, [user]);

  const handleViewProgress = useCallback(
    (postId, videoElement) => {
      if (!videoElement || viewTrackerRef.current.has(postId)) {
        return;
      }

      const post = posts.find((item) => item.id === postId);
      if (!post) return;

      const duration = videoElement.duration || Number(post.duration_seconds) || 0;
      const watchedSeconds = videoElement.currentTime;
      watchProgressRef.current[postId] = watchedSeconds;

      const meetsDuration = watchedSeconds >= VIEW_SECONDS_THRESHOLD;
      const meetsCompletion = duration > 0 && watchedSeconds / duration >= VIEW_COMPLETION_THRESHOLD;

      if (meetsDuration || meetsCompletion) {
        viewTrackerRef.current.add(postId);
        trackEngagement(post, 'view', {
          watched_seconds: Math.round(watchedSeconds),
          duration_seconds: duration ? Math.round(duration) : null,
          percent_viewed: duration ? Number((watchedSeconds / duration).toFixed(2)) : null,
        });
      }
    },
    [posts, trackEngagement]
  );

  const handleFollowToggle = useCallback(async (post) => {
    if (!isAuthenticated) {
      return;
    }

    const creatorId = getCreatorId(post);
    if (!creatorId || creatorId === user?.id) return;

    const shouldFollow = !post.is_following;

    try {
      if (shouldFollow) {
        await apiService.followUser(creatorId);
      } else {
        await apiService.unfollowUser(creatorId);
      }

      setPosts((prev) =>
        updatePostCollection(prev, post.id, (current) => ({
          ...current,
          is_following: shouldFollow,
        }))
      );
    } catch (error) {
      console.error('Failed to toggle follow:', error);
    }
  }, [isAuthenticated, user]);

  // Empty state when no posts
  if (!loading && posts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-black text-white">
        <div className="text-2xl font-bold mb-4">No videos yet</div>
        <p className="text-gray-400 mb-8">Be the first to upload something!</p>
        <Link 
          to="/upload"
          className="px-8 py-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full font-bold hover:opacity-90 transition"
        >
          Upload Video
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-500 border-t-transparent mb-4"></div>
        <div className="text-white text-xl font-semibold">Loading your feed...</div>
        <div className="text-purple-300 text-sm mt-2">Personalizing content just for you</div>
      </div>
    );
  }

  // Video feed view (when posts exist)
  return (
    <div
      className="feed-container bg-black"
      style={{
        height: '100vh', 
        '--safe-area-bottom': 'env(safe-area-inset-bottom)',
      }}
    >
      <style>{`
        .feed-container::-webkit-scrollbar {
          display: none;
        }
        .feed-container {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
      `}</style>

      {/* Mixed Content Warning */}
      <AnimatePresence>
        {showMixedContentWarning && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 max-w-md"
          >
            <div className="bg-yellow-500/90 backdrop-blur text-black px-6 py-4 rounded-2xl shadow-2xl">
              <div className="flex items-start gap-3">
                <span className="text-2xl">⚠️</span>
                <div className="flex-1">
                  <p className="font-bold mb-1">Videos Blocked</p>
                  <p className="text-sm">
                    Your browser only plays media fetched via HTTPS.
                  </p>
                </div>
                <button onClick={() => setShowMixedContentWarning(false)} className="text-black/60 hover:text-black">✕</button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Back Button */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        onClick={() => navigate(-1)}
        className="fixed top-4 left-4 z-50 w-10 h-10 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70 backdrop-blur-sm"
        aria-label="Go back"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
        </svg>
      </motion.button>
      
      {/* Feed Type Filter Tabs - positioned below header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="fixed top-16 left-1/2 -translate-x-1/2 z-30"
      >
        <div className="flex gap-1 bg-black/60 backdrop-blur-sm rounded-full p-1 border border-white/10">
          {[
            { id: 'all', label: 'For You', icon: '✨' },
            { id: 'video', label: 'Video', icon: '🎬' },
            { id: 'music', label: 'Music', icon: '🎵' },
            { id: 'art', label: 'Art', icon: '🎨' },
            { id: 'stories', label: 'Stories', icon: '📖' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setFeedType(tab.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                feedType === tab.id
                  ? 'bg-white text-black'
                  : 'text-white/80 hover:bg-white/10'
              }`}
            >
              <span className="mr-1">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Virtualized Video Feed with Snap Scrolling */}
      <Virtuoso
        data={posts}
        rangeChanged={({ startIndex }) => setCurrentIndex(startIndex)}
        endReached={() => {
          if (hasMore && !loading) {
            loadFeed();
          }
        }}
        overscan={1}
        itemContent={(index, post) => (
          <VideoCard
            key={post.id}
            post={post}
            index={index}
            isActive={index === currentIndex}
            soundOn={soundOn}
            secureContext={secureContext}
            user={user}
            isAuthenticated={isAuthenticated}
            onToggleSound={toggleSound}
            onLike={(p, action) => isAuthenticated && trackEngagement(p, action)}
            onFollow={handleFollowToggle}
            onComment={openComments}
            onShare={(p) => console.log('Share:', p.id)}
            onViewProgress={(postId, metadata) => {
              if (!viewTrackerRef.current.has(postId)) {
                const foundPost = posts.find((item) => item.id === postId);
                if (foundPost) {
                  viewTrackerRef.current.add(postId);
                  trackEngagement(foundPost, 'view', metadata);
                }
              }
            }}
            onVideoRef={(postId, node) => {
              if (node) {
                videoRefs.current[postId] = node;
              } else {
                delete videoRefs.current[postId];
              }
            }}
          />
        )}
        style={{
          height: '100vh',
          scrollSnapType: 'y mandatory',
        }}
        components={{
          Item: React.forwardRef(({ children, ...props }, ref) => (
            <div
              ref={ref}
              {...props}
              style={{
                ...props.style,
                scrollSnapAlign: 'start',
                scrollSnapStop: 'always',
              }}
            >
              {children}
            </div>
          )),
        }}
      />

      {/* End of feed indicator */}
      {!hasMore && posts.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed bottom-24 left-1/2 transform -translate-x-1/2 bg-white/10 backdrop-blur px-6 py-2 rounded-full text-white/60 text-sm"
        >
          You've reached the end
        </motion.div>
      )}

      {/* Loading indicator */}
      {loading && posts.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed bottom-24 left-1/2 transform -translate-x-1/2"
        >
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent" />
        </motion.div>
      )}

      {/* Comments Modal */}
      <CommentsModal
        post={commentsModal.post}
        isOpen={commentsModal.isOpen}
        onClose={closeComments}
      />
    </div>
  );
}

export default Feed;
